import { api } from '@shared/api';
import { camel, type AnalyticsPeriod, type Snake } from './adminAnalyticsApi';

/** Точка когорты: из `eligible` (прошло не меньше N дней с регистрации) вернулись `returned` за дни 1…N. */
export interface CohortCell {
  day: number;
  eligible: number;
  returned: number;
}

export interface Cohort {
  /** YYYY-MM-DD, понедельник недели регистрации (МСК). */
  weekStart: string;
  size: number;
  cells: CohortCell[];
}

export interface ProductStep {
  key: string;
  users: number;
  times: number | null;
  /** С какого дня ведётся источник (события, нажатия); раньше данных нет. */
  trackedSince: string | null;
}

export interface ProductUser {
  id: string;
  name: string;
  email: string;
  registeredAt: string;
  source: string;
  onboarding: boolean;
  verified: boolean;
  cardPresses: number;
  modulesCreated: number;
  wordsAdded: number;
  topicBatches: number;
  trainings: number;
  gameOpened: boolean;
  storiesStarted: number;
  storiesFinished: number;
  missionsStarted: number;
  missionsFinished: number;
  /** Бои с Граммозавром: начато / выиграно (2026-11-01). */
  bossStarted: number;
  bossWon: number;
  dailyTasksTaken: number;
  dailyTasksDone: number;
  activeDays: number;
  lastActive: string | null;
  returned: boolean;
  plan: string;
}

export interface EmailRow {
  kind: string;
  sent: number;
  clicked: number;
  returned: number;
  trained: number;
  holdout: number;
  holdoutReturned: number;
}

export interface ModulesStats {
  createdUsers: number;
  created: number;
  added: { via: string; users: number; count: number }[];
  top: { moduleId: string; title: string; adds: number }[];
  wordsUsers: number;
  words: number;
  wordsOwn: number;
  wordsCopies: number;
  deleted: number;
}

export interface AdminProduct {
  generatedAt: string;
  days: AnalyticsPeriod;
  registered: number;
  returned: number;
  returnedBase: number;
  medianReturnDays: number | null;
  eventsSince: string | null;
  cohorts: Cohort[];
  returnBuckets: { key: string; users: number }[];
  funnel: ProductStep[];
  features: ProductStep[];
  users: ProductUser[];
  emails: EmailRow[];
  modules: ModulesStats | null;
  usersTotal: number;
}

interface BackendProduct {
  generated_at: string;
  days: AnalyticsPeriod;
  registered: number;
  returned: number;
  returned_base: number;
  median_return_days: number | null;
  events_since: string | null;
  cohorts: { week_start: string; size: number; cells: CohortCell[] }[];
  return_buckets: { key: string; users: number }[];
  funnel: Snake<ProductStep>[];
  features: Snake<ProductStep>[];
  users: Snake<ProductUser>[];
  emails?: Snake<EmailRow>[];
  modules?: {
    created_users: number;
    created: number;
    added: { via: string; users: number; count: number }[];
    top: { module_id: string; title: string; adds: number }[];
    words_users: number;
    words: number;
    words_own: number;
    words_copies: number;
    deleted: number;
  } | null;
  users_total: number;
}

/** GET /api/v1/admin/product?days= — аналитика учеников: когорты, возвраты, воронка, функции, письма. */
export async function fetchAdminProduct(days: AnalyticsPeriod): Promise<AdminProduct> {
  const { data } = await api.get<BackendProduct>('/admin/product', { params: { days } });
  return {
    generatedAt: data.generated_at,
    days: data.days,
    registered: data.registered,
    returned: data.returned,
    returnedBase: data.returned_base,
    medianReturnDays: data.median_return_days,
    eventsSince: data.events_since,
    cohorts: data.cohorts.map((row) => ({ weekStart: row.week_start, size: row.size, cells: row.cells })),
    returnBuckets: data.return_buckets,
    funnel: data.funnel.map((row) => camel<ProductStep>(row)),
    features: data.features.map((row) => camel<ProductStep>(row)),
    users: data.users.map((row) => camel<ProductUser>(row)),
    emails: (data.emails ?? []).map((row) => camel<EmailRow>(row)),
    modules: data.modules
      ? {
          createdUsers: data.modules.created_users,
          created: data.modules.created,
          added: data.modules.added,
          top: data.modules.top.map((row) => ({ moduleId: row.module_id, title: row.title, adds: row.adds })),
          wordsUsers: data.modules.words_users,
          words: data.modules.words,
          wordsOwn: data.modules.words_own,
          wordsCopies: data.modules.words_copies,
          deleted: data.modules.deleted,
        }
      : null,
    usersTotal: data.users_total,
  };
}
